import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import {
    Button,
    Card,
    CardTitle,
    Spinner
} from '@/components/ui';
import {
    Input,
    Select,
    ProgressIndicator,
    FileUpload
} from '@/components/forms';
import { PageHeader } from '@/components/layout';
import { ROUTES } from '@/constants/routes';
import { useEquipmentList } from '@/api/useEquipment';
import { useCreateWeeklyReport, useWeeklyReportDetail, useUpdateWeeklyReport } from '@/api/useWeeklyReports';
import type { WeeklyReportData, WeeklyReportEquipmentData } from '@/types/mmms';
import { useAuthStore } from '@/stores/useAuthStore';
import { getCurrentWeekRange } from '@/utils/dateUtils';

import styles from './WeeklyReport.module.css';

const STEPS = [
    { label: 'Selección de Equipos' },
    { label: 'Detalles de Equipos' },
    { label: 'Trabajos Adicionales' },
    { label: 'Revisión y Envío' }
];

const SYSTEMS = [
    { value: 'HIDRÁULICO', label: 'HIDRÁULICO' },
    { value: 'ELÉCTRICO / LUBRICACIÓN', label: 'ELÉCTRICO / LUBRICACIÓN' },
    { value: 'TRANSMISIÓN', label: 'TRANSMISIÓN' },
    { value: 'MOTOR', label: 'MOTOR' },
    { value: 'INYECCIÓN', label: 'INYECCIÓN' },
    { value: 'NEUMÁTICOS', label: 'NEUMÁTICOS' },
    { value: 'CARTUCHOS', label: 'CARTUCHOS' },
    { value: 'NO DA MARCHA', label: 'NO DA MARCHA' },
    { value: 'EN STAND BY', label: 'EN STAND BY' }
];

const QUICK_PHRASES = [
    "Se realizó cambio de mangueras hidráulicas",
    "Se completó nivel de aceite de transmisión",
    "Limpieza de filtros de aire",
    "Ajuste de pernos de tracción",
    "Inspección de fugas en sistema motor",
    "Equipo queda operativo en standby"
];

export function WeeklyReport() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { getUser } = useAuthStore();
    const currentUser = getUser();
    const currentWeek = getCurrentWeekRange();

    const [currentStep, setCurrentStep] = useState(0);
    const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<number[]>([]);

    const { data: equipmentList, isLoading: loadingEquipment } = useEquipmentList();
    const { data: reportDetail, isLoading: loadingDetail } = useWeeklyReportDetail(id || '');
    const { mutate: createReport, isPending: creating } = useCreateWeeklyReport();
    const { mutate: updateReport, isPending: updating } = useUpdateWeeklyReport();

    const isSubmitting = creating || updating;

    // Use setValue for programmatic updates
    const { control, handleSubmit, watch, reset, setValue: setFormValue } = useForm<WeeklyReportData>({
        defaultValues: {
            week_start: currentWeek.startISO,
            week_end: currentWeek.endISO,
            technician_name: currentUser?.username || '',
            status: 'draft',
            equipment_details: []
        }
    });

    const { fields, replace } = useFieldArray({
        control,
        name: "equipment_details"
    });

    const watchedDetails = watch("equipment_details");

    // Load existing report for editing
    useEffect(() => {
        if (isEdit && reportDetail) {
            reset(reportDetail);
            setSelectedEquipmentIds(reportDetail.equipment_details?.map((d: WeeklyReportEquipmentData) => d.equipment_id) || []);
        }
    }, [isEdit, reportDetail, reset]);

    const handleEquipmentToggle = (equipmentId: number) => {
        setSelectedEquipmentIds(prev => {
            if (prev.includes(equipmentId)) {
                return prev.filter(id => id !== equipmentId);
            } else {
                return [...prev, equipmentId];
            }
        });
    };

    const nextStep = () => {
        if (currentStep === 0) {
            // Setup equipment details for selected ones
            const currentDetails = [...watchedDetails];
            const newDetails: WeeklyReportEquipmentData[] = selectedEquipmentIds.map(id => {
                const existing = currentDetails.find((d: WeeklyReportEquipmentData) => d.equipment_id === id);
                if (existing) return existing;

                const equip = equipmentList?.find(e => e.id === id);
                return {
                    equipment_id: id,
                    equipment_code: equip?.code || '',
                    condition_start: 'operative',
                    condition_end: 'operative',
                    hfm_hours: 0,
                    stoppage_count: 1
                };
            });
            replace(newDetails);
        }
        setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
        window.scrollTo(0, 0);
    };

    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 0));
        window.scrollTo(0, 0);
    };

    const onSubmit = (data: WeeklyReportData) => {
        const payload = {
            ...data,
            equipment_details_write: data.equipment_details,
            status: 'submitted' as const
        };

        if (isEdit) {
            updateReport({ id: id!, data: payload }, {
                onSuccess: () => {
                    toast.success('Reporte actualizado con éxito');
                    navigate(ROUTES.home);
                }
            });
        } else {
            createReport(payload, {
                onSuccess: () => {
                    toast.success('Reporte creado con éxito');
                    navigate(ROUTES.home);
                }
            });
        }
    };

    if (loadingEquipment || (isEdit && loadingDetail)) {
        return <div className={styles.loading}><Spinner size="lg" /></div>;
    }

    return (
        <div className={styles.container}>
            <PageHeader
                title={isEdit ? "Editar Reporte Semanal" : "Nuevo Reporte Semanal"}
                subtitle={`Completar información de la semana • ${currentWeek.label}`}
                backPath={ROUTES.home}
            />

            <ProgressIndicator steps={STEPS} currentStep={currentStep} />

            <form onSubmit={handleSubmit(onSubmit)}>
                {currentStep === 0 && (
                    <Card className={styles.stepCard} variant="full">
                        <CardTitle variant="simple">Selección de Equipos</CardTitle>
                        <p className={styles.infoText}>
                            Seleccione ÚNICAMENTE los equipos que tuvieron fallas o inactividad.
                        </p>
                        <div className={styles.categories}>
                            {['JUMBOS', 'SCOOP', 'DUMPER', 'GRUPOS', 'COMPRESORAS'].map(category => {
                                const categoryEquip = equipmentList?.filter(e => e.category === category);
                                if (!categoryEquip?.length) return null;

                                return (
                                    <div key={category} className={styles.categorySection}>
                                        <h3 className={styles.categoryTitle}>
                                            <i className={`fa-solid ${category === 'JUMBOS' ? 'fa-screwdriver-wrench' :
                                                category === 'SCOOP' ? 'fa-shovels' :
                                                    category === 'DUMPER' ? 'fa-truck-moving' :
                                                        category === 'GRUPOS' ? 'fa-charging-station' : 'fa-compact-disc'
                                                }`}></i>
                                            {category}
                                        </h3>
                                        <div className={styles.equipmentGrid}>
                                            {categoryEquip.map(equip => (
                                                <div
                                                    key={equip.id}
                                                    className={`${styles.equipmentItem} ${selectedEquipmentIds.includes(equip.id) ? styles.selected : ''}`}
                                                    onClick={() => handleEquipmentToggle(equip.id)}
                                                >
                                                    <div className={styles.checkbox}>
                                                        {selectedEquipmentIds.includes(equip.id) && <i className="fa-solid fa-check"></i>}
                                                    </div>
                                                    <div className={styles.equipInfo}>
                                                        <span className={styles.equipCode}>{equip.code}</span>
                                                        <span className={styles.equipName}>{equip.name}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>
                )}

                {currentStep === 1 && (
                    <div className={styles.detailsList}>
                        {fields.length === 0 ? (
                            <Card className={styles.emptyCard} variant="full">
                                <p>No se seleccionaron equipos con novedades. Puede continuar al siguiente paso.</p>
                            </Card>
                        ) : (
                            fields.map((field, index) => {
                                const equip = equipmentList?.find(e => e.id === field.equipment_id);
                                const isOperative = watchedDetails[index]?.condition_start === 'operative' && watchedDetails[index]?.condition_end === 'operative';

                                return (
                                    <Card key={field.id} className={styles.detailCard} variant="full">
                                        <div className={styles.detailHeader}>
                                            <span className={styles.detailCode}>{equip?.code}</span>
                                            <span className={styles.detailType}>{equip?.category}</span>
                                        </div>

                                        <div className={styles.formGrid}>
                                            <Controller
                                                name={`equipment_details.${index}.condition_start`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        label="Condición al Inicio"
                                                        value={field.value}
                                                        onChange={(e) => field.onChange(e.target.value)}
                                                        options={[
                                                            { value: 'operative', label: 'Operativo' },
                                                            { value: 'inoperative', label: 'Inoperativo' }
                                                        ]}
                                                    />
                                                )}
                                            />
                                            <Controller
                                                name={`equipment_details.${index}.condition_end`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Select
                                                        label="Condición al Final"
                                                        value={field.value}
                                                        onChange={(e) => field.onChange(e.target.value)}
                                                        options={[
                                                            { value: 'operative', label: 'Operativo' },
                                                            { value: 'inoperative', label: 'Inoperativo' }
                                                        ]}
                                                    />
                                                )}
                                            />
                                        </div>

                                        {!isOperative && (
                                            <div className={`${styles.stoppageSection} fade-in`}>
                                                <div className={styles.formGrid}>
                                                    <Controller
                                                        name={`equipment_details.${index}.hfm_hours`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                name={`hfm-${index}`}
                                                                label="Horas Fuera de Marcha"
                                                                type="number"
                                                                value={field.value.toString()}
                                                                setValue={(v) => field.onChange(parseFloat(v) || 0)}
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        name={`equipment_details.${index}.stoppage_count`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                name={`stops-${index}`}
                                                                label="Número de Paradas"
                                                                type="number"
                                                                value={field.value.toString()}
                                                                setValue={(v) => field.onChange(parseInt(v) || 0)}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className={styles.formGrid}>
                                                    <Controller
                                                        name={`equipment_details.${index}.system_failed`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Select
                                                                label="Sistema que Falló"
                                                                value={field.value || ''}
                                                                onChange={(e) => field.onChange(e.target.value)}
                                                                options={SYSTEMS}
                                                            />
                                                        )}
                                                    />
                                                    <Controller
                                                        name={`equipment_details.${index}.failure_specific`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <Input
                                                                name={`failure-${index}`}
                                                                label="Falla Específica"
                                                                value={field.value || ''}
                                                                setValue={field.onChange}
                                                                placeholder="Describa la falla..."
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className={styles.observationsContainer}>
                                            <Controller
                                                name={`equipment_details.${index}.observations`}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        name={`obs-${index}`}
                                                        label="Trabajo Realizado / Observaciones"
                                                        multiline
                                                        rows={3}
                                                        value={field.value || ''}
                                                        setValue={field.onChange}
                                                        placeholder="Describa los trabajos realizados..."
                                                    />
                                                )}
                                            />
                                            <div className={styles.quickPhrases}>
                                                <span className={styles.quickPhrasesLabel}>Frases rápidas:</span>
                                                <div className={styles.phrasesGrid}>
                                                    {QUICK_PHRASES.map(phrase => (
                                                        <button
                                                            key={phrase}
                                                            type="button"
                                                            className={styles.phraseBtn}
                                                            onClick={() => {
                                                                const currentVal = watchedDetails[index]?.observations || '';
                                                                const newVal = currentVal ? `${currentVal}. ${phrase}` : phrase;
                                                                setFormValue(`equipment_details.${index}.observations`, newVal);
                                                            }}
                                                        >
                                                            {phrase}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.photoUploadPlaceholder}>
                                            <Controller
                                                name={`equipment_details.${index}.evidence_file`}
                                                control={control}
                                                render={({ field }) => (
                                                    <FileUpload
                                                        name={`evidence-${index}`}
                                                        label="Evidencia Fotográfica"
                                                        accept="image/*"
                                                        file={field.value || null}
                                                        setFile={field.onChange}
                                                        description="Adjunte foto de evidencia"
                                                    />
                                                )}
                                            />
                                        </div>

                                        <div className={styles.availabilityBox}>
                                            <span className={styles.availabilityLabel}>Disponibilidad Física:</span>
                                            <span className={styles.availabilityValue}>
                                                {(() => {
                                                    const hfm = watchedDetails[index]?.hfm_hours || 0;
                                                    const availability = ((168 - hfm) / 168 * 100).toFixed(1);
                                                    return `${availability}%`;
                                                })()}
                                            </span>
                                        </div>
                                    </Card>
                                );
                            })
                        )}
                    </div>
                )}

                {currentStep === 2 && (
                    <Card className={styles.stepCard} variant="full">
                        <CardTitle variant="simple">Trabajos Adicionales</CardTitle>
                        <Controller
                            name="electrical_works"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    name="electrical_works"
                                    label="Trabajos Eléctricos (Opcional)"
                                    placeholder="Describa los trabajos eléctricos..."
                                    multiline
                                    rows={3}
                                    value={field.value || ''}
                                    setValue={field.onChange}
                                />
                            )}
                        />
                        <Controller
                            name="other_works"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    name="other_works"
                                    label="Otros Trabajos (Opcional)"
                                    placeholder="Describa los otros trabajos..."
                                    multiline
                                    rows={3}
                                    value={field.value || ''}
                                    setValue={field.onChange}
                                />
                            )}
                        />
                        <Controller
                            name="workshop_notes"
                            control={control}
                            render={({ field }) => (
                                <Input
                                    name="workshop_notes"
                                    label="Notas del Taller (Opcional)"
                                    placeholder="Describa las notas del taller..."
                                    multiline
                                    rows={2}
                                    value={field.value || ''}
                                    setValue={field.onChange}
                                />
                            )}
                        />
                    </Card>
                )}

                {currentStep === 3 && (
                    <Card className={styles.stepCard} variant="full">
                        <CardTitle variant="simple">Resumen del Reporte</CardTitle>
                        <div className={styles.summaryInfo}>
                            <p><strong>Técnico:</strong> {watch("technician_name")}</p>
                            <p><strong>Fecha:</strong> {watch("week_start")} - {watch("week_end")}</p>
                            <div className={styles.divider}></div>
                            <p><strong>Equipos con novedades:</strong> {fields.length}</p>
                            <ul className={styles.summaryList}>
                                {fields.map((f, i) => (
                                    <li key={f.id}>{watchedDetails[i]?.equipment_code} - {watchedDetails[i]?.system_failed || 'Sin sistema'}</li>
                                ))}
                            </ul>
                        </div>
                        <div className={styles.verificationBox}>
                            <p className={styles.warningText}>
                                Una vez enviado, el reporte no podrá ser editado. Verifique que toda la información sea correcta.
                            </p>
                        </div>
                    </Card>
                )}

                <div className={styles.actions}>
                    {currentStep > 0 && (
                        <Button type="button" variant="secondary" onClick={prevStep}>
                            Anterior
                        </Button>
                    )}
                    <span className={styles.spacer}></span>
                    {currentStep < STEPS.length - 1 ? (
                        <Button type="button" onClick={nextStep} disabled={currentStep === 0 && selectedEquipmentIds.length === 0 && false}>
                            Continuar
                        </Button>
                    ) : (
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner variant="secondary" size="sm" /> : (isEdit ? 'Actualizar Reporte' : 'Enviar Reporte')}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
}
